import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4">
        <div className="text-center">
          <p className="text-[#595959] mb-4">Invalid reset link. No token provided.</p>
          <Link to="/forgot-password" className="text-[#E68A00] font-medium hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPw) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#E68A00] rounded-md flex items-center justify-center mx-auto mb-4">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Set New Password
          </h1>
        </div>

        {success ? (
          <div className="bg-white border border-[#E5E0D8] rounded-lg p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Password Updated!
            </h2>
            <p className="text-sm text-[#595959] mb-4">Redirecting you to login...</p>
            <Link to="/login" className="text-[#E68A00] font-medium text-sm hover:underline" data-testid="goto-login-link">
              Go to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-[#E5E0D8] rounded-lg p-6 space-y-4">
            {error && (
              <div data-testid="reset-pw-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="new-pw" className="text-[#1C1C1C]">New Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="new-pw"
                  data-testid="reset-pw-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="bg-white border-[#E5E0D8] focus:ring-[#E68A00] pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#595959]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-pw" className="text-[#1C1C1C]">Confirm Password</Label>
              <Input
                id="confirm-pw"
                data-testid="reset-pw-confirm"
                type={showPw ? "text" : "password"}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Re-enter password"
                required
                className="mt-1.5 bg-white border-[#E5E0D8] focus:ring-[#E68A00]"
              />
            </div>
            <button
              type="submit"
              data-testid="reset-pw-submit"
              disabled={loading}
              className="w-full bg-[#E68A00] hover:bg-[#CC7A00] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
